import React, { useState, useEffect } from 'react';
import { Grid, Text } from 'elements';
import { showConfirm } from 'components/modal/showConfirm';
import { TReply } from 'modules/case/types';
import { Button, Pagination } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import { Colors } from 'GlobalStyles';
import Edit from './Edit';
import Item from './Item';

type TReplyParams = {
  page_type?: string;
  id: number;
  limit?: number;
  getReply: (id: number, isSub: boolean, params: { pageSize: number; current: number }) => Promise<any>;
  onSave: (id: number, content: string, isSub: boolean) => Promise<any>;
  onEdit: (id: number, content: string, isSub: boolean) => Promise<any>;
  like: (id: number, isLike: boolean, isSub: boolean) => Promise<any>;
  del: (replyId: number, isSub: boolean) => Promise<any>;
};

const Reply = ({ page_type, id, limit = 5, getReply, onSave, onEdit, like, del }: TReplyParams) => {
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [replies, setReplies] = useState<Array<TReply>>();
  const [subReplies, setSubReplies] = useState<{ [key: string]: Array<any> }>({});
  const [openedReply, setOpenedReply] = useState<number>();

  const callGetReply = async (page = 1, parentId?: number) => {
    setPage(page);
    const { success, data } = await getReply(parentId ?? id, parentId !== undefined, {
      pageSize: limit,
      current: page,
    });
    if (success) {
      if (parentId === undefined) {
        setTotal(data.total);
        setReplies(data.contents);
      } else {
        setSubReplies((subReplies: any) => {
          subReplies[parentId] = data;
          return { ...subReplies };
        });
      }
    }
  };

  const callSaveReply = async (body: string, replyId?: number) => {
    const { success } = await onSave(replyId ?? id, body, replyId !== undefined);
    if (success) {
      if (replyId === undefined) {
        const lastPage = Math.ceil((total + 1) / limit);
        if (lastPage === page) callGetReply(lastPage);
        else callGetReply(Math.ceil((total + 1) / limit));
      } else {
        callGetReply(page);
        callGetReply(page, replyId);
        setOpenedReply(replyId);
      }
    }
  };

  const callEditReply = async (id: number, body: string, parentId?: number) => {
    const { success } = await onEdit(id, body, parentId !== undefined);
    if (success) callGetReply(page, parentId);
  };

  const callLike = async (id: number, isLike: boolean, parentId?: number) => {
    const { success, data } = await like(id, isLike, parentId !== undefined);
    if (success) {
      //parentId가 없다는 것은 대댓글이 아니라는 것
      if (parentId === undefined) {
        //좋아요를 누르면 replies들 중에서 좋아요 누른 댓글 id값을 골라내서 데이터를 갈야 끼워넣는다(좋아요 누른 것 실시간 반영)
        setReplies(replies?.map((r) => (r.id === id ? data : r)));
      } else {
        //parentID 가 있다는 것은 대댓글이라는 것
        setSubReplies({
          ...subReplies,
          //하나의 객체로 오는데 객체의 키값이 부모 댓글의 id(parentId)
          [parentId]: subReplies[parentId].map((r) => {
            if (r.id === data.id) return data;
            return r;
          }),
        });
      }
    }
  };

  const callMainCommentLike = async (id: number) => {
    const { success, data } = await like(id, true, false);
    if (success) {
      //좋아요를 누르면 replies들 중에서 좋아요 누른 댓글 id값을 골라내서 데이터를 갈야 끼워넣는다(좋아요 누른 것 실시간 반영)
      setReplies(replies?.map((r) => (r.id === id ? data : r)));
    }
  };

  const callDel = (id: number, parentId?: number) => {
    showConfirm(
      async () => {
        const { success } = await del(id, parentId !== undefined);
        if (success) {
          if (parentId === undefined) callGetReply(1);
          else {
            callGetReply(page);
            callGetReply(page, parentId);
          }
        }
      },
      '댓글 삭제',
      '댓글을 삭제하시겠습니까?',
    );
  };

  useEffect(() => {
    callGetReply();
  }, []);

  useEffect(() => {
    if (openedReply) callGetReply(1, openedReply);
  }, [openedReply]);

  return page_type === 'caseQNA' ? (
    <Grid>
      <Grid>
        <Text>Comments: {total}</Text>
      </Grid>
      {!!replies && replies.length > 0 && (
        <>
          {replies.map((reply) => (
            <Grid key={reply.id} padding='12px 0' style={{ borderBottom: `1px solid ${Colors.grey1}` }}>
              <Item
                page_type={page_type}
                reply={reply}
                canAddReply
                onSave={(body) => callSaveReply(body, reply.id)}
                onEdit={(body) => callEditReply(reply.id, body)}
                // like={() => callLike(reply.id, true)}
                dislike={() => callMainCommentLike(reply.id)}
                like={() => callMainCommentLike(reply.id)}
                del={callDel}
              />
              {!!reply.reply_count && reply.reply_count > 0 && (
                <Grid marginTop='8px'>
                  <Button
                    type='text'
                    className='text'
                    onClick={() => setOpenedReply(openedReply === reply.id ? undefined : reply.id)}
                  >
                    <Grid gap='8px' align='center'>
                      {openedReply === reply.id ? <CaretUpOutlined /> : <CaretDownOutlined />}
                      <Text>
                        답글 {reply.reply_count}개 {openedReply === reply.id ? '숨기기' : '열기'}
                      </Text>
                    </Grid>
                  </Button>
                </Grid>
              )}
              {openedReply === reply.id && !!subReplies && !!subReplies[reply.id] && (
                <Grid padding='10px 25px' gap='15px'>
                  {!!subReplies &&
                    !!subReplies[reply.id] &&
                    subReplies[reply.id].map((subReply: TReply) => (
                      <Item
                        isSubReplies={true}
                        page_type={page_type}
                        key={subReply.id}
                        reply={subReply}
                        canAddReply
                        onSave={(body) => callSaveReply(body, reply.id)}
                        onEdit={(body) => callEditReply(subReply.id, body, reply.id)}
                        like={() => callLike(subReply.id, true, reply.id)}
                        dislike={() => callLike(subReply.id, false, reply.id)}
                        del={() => callDel(subReply.id, reply.id)}
                      />
                    ))}
                </Grid>
              )}
            </Grid>
          ))}
          <Grid marginTop='40px' justify='center'>
            <Pagination
              current={page}
              pageSize={limit}
              responsive
              total={total}
              onChange={(page) => callGetReply(page)}
            />
          </Grid>
        </>
      )}
      <Grid marginTop='40px'>
        <Edit onSave={callSaveReply} />
      </Grid>
    </Grid>
  ) : (
    <Grid>
      <Grid>
        <Text>Comments: {total}</Text>
      </Grid>
      {!!replies && replies.length > 0 && (
        <>
          {replies.map((reply) => (
            <Grid key={reply.id} padding='12px 0' style={{ borderBottom: `1px solid ${Colors.grey1}` }}>
              <Item
                page_type={page_type}
                reply={reply}
                canAddReply
                onSave={(body) => callSaveReply(body, reply.id)}
                onEdit={(body) => callEditReply(reply.id, body)}
                like={() => callLike(reply.id, true)}
                dislike={() => callLike(reply.id, false)}
                del={callDel}
              />
              {!!reply.comment_review_count && reply.comment_review_count > 0 && (
                <Grid marginTop='8px'>
                  <Button
                    type='text'
                    className='text'
                    onClick={() => setOpenedReply(openedReply === reply.id ? undefined : reply.id)}
                  >
                    <Grid gap='8px' align='center'>
                      {openedReply === reply.id ? <CaretUpOutlined /> : <CaretDownOutlined />}
                      <Text>
                        답글 {reply.comment_review_count}개 {openedReply === reply.id ? '숨기기' : '열기'}
                      </Text>
                    </Grid>
                  </Button>
                </Grid>
              )}
              {openedReply === reply.id && !!subReplies && !!subReplies[reply.id] && (
                <Grid padding='10px 25px' gap='15px'>
                  {!!subReplies &&
                    !!subReplies[reply.id] &&
                    subReplies[reply.id].map((subReply: TReply) => (
                      <Item
                        page_type={page_type}
                        key={subReply.id}
                        reply={subReply}
                        canAddReply
                        onSave={(body) => callSaveReply(body, reply.id)}
                        onEdit={(body) => callEditReply(subReply.id, body, reply.id)}
                        like={() => callLike(subReply.id, true, reply.id)}
                        dislike={() => callLike(subReply.id, false, reply.id)}
                        del={() => callDel(subReply.id, reply.id)}
                      />
                    ))}
                </Grid>
              )}
            </Grid>
          ))}
          <Grid marginTop='40px' justify='center'>
            <Pagination
              current={page}
              pageSize={limit}
              responsive
              total={total}
              onChange={(page) => callGetReply(page)}
            />
          </Grid>
        </>
      )}
      <Grid marginTop='40px'>
        <Edit onSave={callSaveReply} />
      </Grid>
    </Grid>
  );
};

export default Reply;
